// Starter policy content. These are sensible defaults for a UK domiciliary
// care company regulated by the CQC — they are NOT a substitute for review
// by a solicitor. Edit freely.
//
// Each policy is rendered by /app/frontend/src/pages/PolicyPage.jsx.

const LAST_UPDATED = "January 2026";

export const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    intro:
      "SeaShells Care takes the privacy of every family who contacts us seriously. This policy explains what information we collect, how we use it, and your rights under UK GDPR and the Data Protection Act 2018.",
    sections: [
      {
        h: "1. Who we are",
        p: [
          "SeaShells Care is a domiciliary and live-in care provider registered with and regulated by the Care Quality Commission (CQC), covering Epping Forest, Harlow and the surrounding Essex area.",
          "For the purposes of UK GDPR, SeaShells Care is the data controller of any personal data you share with us via this website. You can contact us at info@seashellcare.com or 01992 892894.",
        ],
      },
      {
        h: "2. What information we collect",
        p: [
          "When you submit our enquiry form we collect: your name, phone number, postcode, optional email address, the type of care you're interested in, and any message you provide.",
          "When you submit a career application we collect: your name, phone, email, role of interest, experience and any covering note.",
          "When you visit the site, like most websites we also collect limited technical information automatically (e.g. pages viewed, device type, referrer) via Google Analytics — see our Cookies Policy for details.",
        ],
      },
      {
        h: "3. How we use your information",
        p: [
          "To respond to your enquiry, arrange a free home assessment, and (where you become a client) deliver care services to your family.",
          "To assess and respond to career applications.",
          "To monitor and improve the website and our service quality.",
        ],
      },
      {
        h: "4. Legal basis for processing",
        p: [
          "Legitimate interests — to respond to enquiries you have actively sent us.",
          "Contract — where you become a client of SeaShells Care, to perform the care contract between us.",
          "Legal obligation — to meet our regulatory duties under the Health and Social Care Act 2008 and CQC requirements.",
        ],
      },
      {
        h: "5. Who we share information with",
        p: [
          "We do not sell your personal data. We share it only with carefully selected service providers that help us run the business — including our email-notification provider (Resend), our hosting and DNS provider (Cloudflare), and our analytics provider (Google Analytics). Each of these is bound by data-protection agreements.",
          "We will disclose personal data to authorities such as the CQC, the police or social services where we are legally required to, or where there is a genuine safeguarding concern.",
        ],
      },
      {
        h: "6. How long we keep it",
        p: [
          "Enquiries that don't become care arrangements: 12 months from your last contact, then deleted.",
          "Client records: for the duration of the care contract plus 8 years thereafter, in line with NHS-aligned care-records guidance.",
          "Career applications: 6 months unless you've asked us to keep your details on file.",
        ],
      },
      {
        h: "7. Your rights",
        p: [
          "Under UK GDPR you have the right to ask us to: see what information we hold about you, correct anything inaccurate, delete your information (in most circumstances), restrict how we use it, object to processing, or receive it in a portable format.",
          "Email info@seashellcare.com to exercise any of these rights — we'll respond within one calendar month.",
          "You also have the right to complain to the Information Commissioner's Office at ico.org.uk if you believe we have mishandled your data — though we'd appreciate the chance to put things right first.",
        ],
      },
      {
        h: "8. Cookies",
        p: [
          "We use a small number of cookies, primarily for analytics. See our separate Cookies Policy for details.",
        ],
      },
      {
        h: "9. Changes to this policy",
        p: [
          `This policy was last updated in ${LAST_UPDATED}. We'll update it when we make material changes to how we handle data.`,
        ],
      },
    ],
  },

  cookies: {
    title: "Cookies Policy",
    intro:
      "This page explains what cookies are, the cookies SeaShells Care uses on this website, and how you can control them.",
    sections: [
      {
        h: "1. What cookies are",
        p: [
          "Cookies are small text files that websites place on your device so they can recognise you on return visits, remember your preferences, and measure how the site is used. Most modern websites use them.",
        ],
      },
      {
        h: "2. The cookies we use",
        p: [
          "Strictly necessary — used to make the website work, remember your interactions during a single visit, and keep things secure. These cannot be switched off.",
          "Analytics — we use Google Analytics (via Google Tag Manager) to understand which pages are useful, how visitors find us, and where we can improve. These cookies don't identify you personally to us — only to Google.",
          "We do not use advertising / re-targeting cookies on this site.",
        ],
      },
      {
        h: "3. Controlling cookies",
        p: [
          "You can refuse cookies in your browser settings. All modern browsers let you block all cookies, block third-party cookies, or delete cookies after each session.",
          "If you block cookies entirely, the website will still work — you'll just appear to us as a brand-new visitor every time.",
        ],
      },
      {
        h: "4. Changes",
        p: [
          `This Cookies Policy was last updated in ${LAST_UPDATED}.`,
        ],
      },
    ],
  },

  safeguarding: {
    title: "Safeguarding Policy",
    intro:
      "Safeguarding sits at the heart of everything we do. SeaShells Care is committed to protecting the people we care for — and the people who care for us — from abuse, neglect and harm of any kind.",
    sections: [
      {
        h: "1. Our commitment",
        p: [
          "We believe that every adult we support has the right to live in safety, free from abuse and neglect. We comply fully with the Care Act 2014, the Mental Capacity Act 2005, and all guidance issued by the Care Quality Commission (CQC) and Essex Safeguarding Adults Board.",
        ],
      },
      {
        h: "2. Our carers",
        p: [
          "Every SeaShells Care employee is subject to an enhanced DBS (Disclosure and Barring Service) check before they ever set foot in a client's home, and we re-check at regular intervals.",
          "Every carer completes safeguarding training as part of induction and refreshes it annually. They also complete the Care Certificate, dementia training, moving & handling, and medication training.",
          "Carers are supervised by a named senior carer and have direct access to our management team 24/7.",
        ],
      },
      {
        h: "3. Reporting a concern",
        p: [
          "If you are worried about the safety or wellbeing of someone in our care, please contact us immediately on 01992 892894 or info@seashellcare.com. We treat every concern seriously and confidentially.",
          "You can also contact the Essex County Council Adult Social Care safeguarding team directly on 0345 603 7630 (24/7 line) or the police on 999 in an emergency.",
          "If you wish to raise a concern about SeaShells Care itself, you can also report directly to the Care Quality Commission at cqc.org.uk or 03000 616161.",
        ],
      },
      {
        h: "4. Our promise to you",
        p: [
          "We will always listen. We will always act. We will keep you informed of every step. And we will always learn from feedback — because the families who trust us deserve nothing less.",
        ],
      },
      {
        h: "5. Review",
        p: [
          `This policy is reviewed annually and was last updated in ${LAST_UPDATED}.`,
        ],
      },
    ],
  },
};

export const findPolicy = (slug) => POLICIES[slug];
